'use client';

import { isMobile } from 'react-device-detect';
import { Product } from '@interface/product';
import CheckHydration from '@utils/CheckHydration';
import LazyYouTube from 'app/(web)/components/common/LazyYouTube';
import { Container, Flex } from 'designSystem/Layouts/Layouts';
import { Text, Title } from 'designSystem/Texts/Texts';
import dynamic from 'next/dynamic';

const Carousel = dynamic(() => import('designSystem/Carousel/Carousel'), {
  ssr: false,
});

export default function ProductVideos({
  product,
  videos,
  title = 'Holaglow lovers',
}: {
  product?: Product;
  videos?: { url: string; active: boolean }[];
  title?: string;
}) {
  const genericVideo = { url: 'MOErpjePkRw', active: true };

  // Copia: antes se hacía push sobre product.videos y se mutaba la prop.
  const finalVideos = [...(product?.videos || videos || [])];

  if (!finalVideos.find(video => video.url === genericVideo.url)) {
    finalVideos.push(genericVideo as any);
  }

  const activeVideos = finalVideos.filter(video => video.active);

  if (activeVideos.length === 0) {
    return <></>;
  }

  const isSingle = activeVideos.length <= 1;

  // El carrusel solo se pinta en cliente (ssr:false + CheckHydration), así que
  // `isMobile` se evalúa ya con el user-agent real y la página puede ser ISR.
  const showControls = isMobile ? !isSingle : activeVideos.length > 3;

  return (
    <Container className="px-0 md:px-4 py-12 md:py-16">
      <Flex
        layout="col-left"
        className={`w-full ${isSingle ? 'md:flex-row md:gap-16' : ''}`}
      >
        <div className={isSingle ? 'px-4 md:w-1/2' : 'px-4'}>
          <Title as="h2" size="2xl" className="font-bold mb-6">
            {title}
          </Title>
          <Text className="text-hg-black500 md:text-lg mb-8">
            Descubre la experiencia en primera persona de pacientes que ya han
            confiado en Holaglow.
          </Text>
        </div>
        <div className={isSingle ? 'w-full md:w-1/3' : 'w-full'}>
          <CheckHydration>
            <Carousel
              hasControls={showControls}
              dragEnabled={showControls}
              touchEnabled={showControls}
              visibleSlides={isSingle ? 1 : isMobile ? 1.2 : 3}
              isIntrinsicHeight
              infinite={false}
              sliderStyles="md:gap-8"
              controlstyles="px-4"
            >
              {activeVideos.map(video => {
                const videoId = video.url.split('/').pop() || video.url;

                return (
                  <div key={video.url} className="px-4 w-full pdpVideoSlide">
                    {/* Miniatura + play: el iframe de YouTube (~400 KB) solo
                        se carga si el usuario quiere ver el vídeo. */}
                    <LazyYouTube
                      videoId={videoId}
                      title={`${product?.title ?? 'Holaglow'}: la experiencia de una paciente`}
                      aspectClassName="aspect-[9/16] max-h-[560px]"
                      className={
                        isMobile && !isSingle ? 'w-full' : 'w-full md:max-w-[315px]'
                      }
                    />
                  </div>
                );
              })}
            </Carousel>
          </CheckHydration>
        </div>
      </Flex>
    </Container>
  );
}
